import isUndefined from 'lodash-es/isUndefined';
import { isBlockLevelContent } from './dom/content';
import { CSS_NON_INHERITED_PROPERTIES } from './dom/css';
import {
  isContentEditableInEditor,
  isElement,
  isHTMLOrSVGElement,
  isText,
  removeAllChildren,
} from './dom/node';
import {
  convertTraditionalParagraphElement,
  isBreakElement,
  isParagraphElement,
  isParagraphEmpty,
  isTraditionalParagraphElement,
} from './dom/paragraph';
import { nodeToLines } from './htmlToText';
import { linesToDocumentFragment } from './textToHtml';

export interface FlattenParagraphsOptions {
  shouldConvertBlocksToParagraphs?: boolean;
  maxParagraphCount?: number;
}

export function flattenParagraphs(
  container: Node,
  options: FlattenParagraphsOptions = {},
  parent: Node = container,
) {
  const document = container.ownerDocument;
  if (!document) {
    return;
  }

  const shouldConvertBlocksToParagraphs = Boolean(options.shouldConvertBlocksToParagraphs);
  const newOptions = { ...options };

  Array.from(parent.childNodes).forEach(childNode => {
    if (!isElement(childNode)) {
      return;
    }

    let childElement = childNode;

    // Flatten our children first.
    flattenParagraphs(container, newOptions, childElement);

    // Convert traditional paragraphs (P) to our concept of paragraphs (DIV).
    if (isTraditionalParagraphElement(childElement)) {
      childElement = convertTraditionalParagraphElement(childElement);
    }

    // Convert non-paragraph blocks to paragraphs using our htmlToText and textToHtml helpers.
    if (
      shouldConvertBlocksToParagraphs &&
      isBlockLevelContent(childElement) &&
      !isParagraphElement(childElement) &&
      childElement.parentNode
    ) {
      const lines = nodeToLines(childNode);
      const fragment = linesToDocumentFragment(document, lines, {
        lineStyle: childElement.getAttribute('style'),
      });
      childElement.parentNode.insertBefore(fragment, childElement);
      childElement.parentNode.removeChild(childElement);
      return;
    }

    // Move on if this is not a paragraph, or if it has styles that cannot be pushed down.
    if (!isParagraphElement(childElement) || hasNonInheritableStyle(childElement)) {
      return;
    }

    // Move on if this is not content editable.
    if (!isContentEditableInEditor(childElement, container)) {
      return;
    }

    if (!isUndefined(newOptions.maxParagraphCount)) {
      if (newOptions.maxParagraphCount > 0) {
        newOptions.maxParagraphCount -= 1;
      } else {
        return;
      }
    }

    // Remove empty-ish paragraphs with a 1px font size on them.
    const { fontSize } = childElement.style;
    if (
      fontSize &&
      fontSize.endsWith('px') &&
      parseFloat(fontSize) < 1.1 &&
      (isParagraphEmpty(childElement) ||
        (childElement.childNodes.length === 1 && isBreakElement(childElement.firstChild))) &&
      childElement.parentNode
    ) {
      childElement.parentNode.removeChild(childElement);
      return;
    }

    // This appears to be a normal paragraph, so flatten it!
    flattenParagraph(childElement);
  });
}

function flattenParagraph(element: HTMLDivElement): void {
  // If this paragraph is empty, nothing else to do either. It might be removed by a parent paragraph, though.
  if (isParagraphEmpty(element)) {
    return;
  }

  const originalParagraph = element;
  const { parentNode } = element;
  // istanbul ignore if
  if (!parentNode) {
    return;
  }

  // Helper state and function for inserting new nodes before/after/at the paragraph.
  let nextSibling: Node | null = element;
  const insertBlock = (newChild: Node) => {
    // If we're inserting the next sibling itself (typically the paragraph) then simply move the "cursor" ahead.
    if (newChild === nextSibling) {
      nextSibling = newChild.nextSibling;
    } else {
      parentNode.insertBefore(newChild, nextSibling);
    }
  };

  // Helper state and function for the current paragraph we're building.
  let currentParagraph: HTMLDivElement | undefined;
  const maybeFlushParagraph = () => {
    if (currentParagraph) {
      if (!isParagraphEmpty(currentParagraph)) {
        insertBlock(currentParagraph);
      } else if (currentParagraph.parentNode) {
        currentParagraph.parentNode.removeChild(currentParagraph);
      }
      currentParagraph = undefined;
    }
  };

  Array.from(originalParagraph.childNodes).forEach(childNode => {
    // Unwrap block-level children.
    if (isBlockLevelContent(childNode)) {
      // If we got a paragraph going, finish it here, essentially inserting a line break before this non-inline child.
      maybeFlushParagraph();

      // Discard empty paragraphs.
      if (isParagraphElement(childNode) && isParagraphEmpty(childNode)) {
        originalParagraph.removeChild(childNode);
      } else {
        maybePrependInheritedStyle(originalParagraph, childNode);
        insertBlock(childNode);
      }
      return;
    }

    // Obtain a paragraph to add this inline child to.
    if (!currentParagraph) {
      // If this is the first child of our original paragraph, and we're still inserting before the original
      // paragraph, reuse it. Otherwise, create a new paragraph.
      if (nextSibling === originalParagraph && childNode === originalParagraph.firstChild) {
        currentParagraph = originalParagraph;
      } else {
        currentParagraph = originalParagraph.cloneNode(false) as typeof originalParagraph;
      }
    }

    const isOriginalParagraph = currentParagraph === originalParagraph;

    // Handle BR tags.
    if (isBreakElement(childNode)) {
      // Remove cruft.
      const { className } = childNode;
      const newClassName = className.replace(/\bApple-interchange-newline\b\s*/, '');
      if (className !== newClassName) {
        if (newClassName) {
          // eslint-disable-next-line no-param-reassign
          childNode.className = newClassName;
        } else {
          childNode.removeAttribute('class');
        }
      }

      const previousSibling = isOriginalParagraph ? childNode.previousSibling : currentParagraph.lastChild;

      if (isParagraphEmpty(currentParagraph)) {
        // If the current paragraph is empty, keep the BR tag as filler.
        removeAllChildren(currentParagraph);
        currentParagraph.appendChild(childNode);
      } else if (childNode === currentParagraph.firstChild) {
        // If this BR tag is the first child of the original paragraph, keep the BR tag as filler.
      } else if (isText(previousSibling) && previousSibling.data.endsWith(' ')) {
        // If this BR tag follows a text node ending in whitespace, keep the BR tag as filler no matter which
        // paragraph we're in.
        if (!isOriginalParagraph) {
          currentParagraph.appendChild(childNode);
        }
      } else {
        // If none of the above applies, get rid of it.
        originalParagraph.removeChild(childNode);
      }

      // Start a new paragraph.
      maybeFlushParagraph();
      return;
    }

    if (!isOriginalParagraph) {
      currentParagraph.appendChild(childNode);
    }
  });

  maybeFlushParagraph();

  // If after all of this, the original paragraph became empty, discard it.
  if (element.parentNode && isParagraphEmpty(element)) {
    element.parentNode.removeChild(element);
  }
}

const WHITELISTED_CSS_NON_INHERITED_PROPERTIES = new Set(['margin-left', 'margin']);

function hasNonInheritableStyle(element: HTMLDivElement): boolean {
  const { style } = element;
  for (let i = 0; i < style.length; i++) {
    const propertyName = style[i];
    if (
      !WHITELISTED_CSS_NON_INHERITED_PROPERTIES.has(propertyName) &&
      CSS_NON_INHERITED_PROPERTIES.has(propertyName)
    ) {
      return true;
    }
  }
  return false;
}

const INHERITABLE_ATTRIBUTES = new Set(['align']);

function maybePrependInheritedStyleAttributes(
  ancestorElement: HTMLElement | SVGElement,
  childNode: HTMLElement | SVGElement,
) {
  if (!ancestorElement.hasAttributes()) {
    return;
  }

  Array.from(ancestorElement.attributes)
    .filter(attr => INHERITABLE_ATTRIBUTES.has(attr.name))
    .forEach(attr => childNode.setAttribute(attr.name, attr.value));
}

function maybePrependInheritedStyle(ancestorElement: HTMLElement | SVGElement, childNode: Node) {
  if (!isHTMLOrSVGElement(childNode)) {
    return;
  }

  maybePrependInheritedStyleAttributes(ancestorElement, childNode);

  const inheritedStyle = ancestorElement.getAttribute('style');
  if (!inheritedStyle) {
    return;
  }

  const childStyle = childNode.getAttribute('style');
  const newChildStyle = childStyle ? `${inheritedStyle.replace(/;\s*$/, '')}; ${childStyle}` : inheritedStyle;
  childNode.setAttribute('style', newChildStyle);
}
